
import { dateHelper } from '@/helpers/dateHelper'
import CollectService from '@/services/collect/collectService'
import { SnackBarType } from '@/store/types'
import { Collect, Day, Slot, WeekDay } from '@/types'
import { OperationUnit, StartEndOf } from '@meltingpoint/lastmile-internationalization'
import Vue from 'vue'

const weekIndex = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']

export default Vue.extend({
  name: 'mp-drive-pickup-dsk',
  props: {
    collect: {
      type: Object as () => Collect,
      required: true,
    },
    language: {
      required: true,
    },
  },
  data() {
    return {
      hasLoaded: false,
      week: {
        start: null as Date | null,
        end: null as Date | null,
      },
      weekDay: {} as WeekDay,
      dateHelper,
      initialWeek: true,
      selectedSlot: null as Slot | null,
      isChoosedSlot: false,
      weekIndex,
    }
  },
  computed: {
    weekDisplay(): string {
      return `${this.week.start!.getDate()} - ${dateHelper.format(this.week.end!, 'PPP')}`
    },

    getSelectedSlotDisplay(): string {
      this.isChoosedSlot =
        this.collect.start === this.selectedSlot?.start &&
        this.collect.end === this.selectedSlot?.end

      return `${dateHelper.format(
        this.selectedSlot?.start!,
        'EEEE d LLLL p',
      )} - ${dateHelper.format(this.selectedSlot?.end!, 'p')}`
    },
  },
  async mounted() {
    if (
      this.collect.start &&
      dateHelper.isSameOrAfter(this.collect.start, dateHelper.getDate(), OperationUnit.DAYS)
    ) {
      this.week.start =
        dateHelper.startOf(dateHelper.getDate(this.collect.start), StartEndOf.WEEKS) || null
      this.week.end = dateHelper.endOf(this.week.start!, StartEndOf.WEEKS) || null

      this.initialWeek = dateHelper.isDateInInterval(dateHelper.getDate(), {
        start: this.week.start!,
        end: this.week.end!,
      })

      this.generateWeek()
    } else if (this.collect.firstAvailableSlot && dateHelper.isSameOrAfter(this.collect.firstAvailableSlot, dateHelper.getDate(), OperationUnit.DAYS)) {
      this.week.start = dateHelper.startOf(dateHelper.getDate(this.collect.firstAvailableSlot), StartEndOf.WEEKS) || null
      this.week.end = dateHelper.endOf(dateHelper.getDate(this.collect.firstAvailableSlot), StartEndOf.WEEKS) || null
      this.initialWeek = dateHelper.isDateInInterval(dateHelper.getDate(), {
        start: this.week.start!,
        end: this.week.end!,
      })

      this.generateWeek()
    } else {
      this.goToThisWeek()
    }
  },
  methods: {
    changeWeek(next = false): void {
      if (next) {
        this.week.start = dateHelper.add(this.week.start!, { days: 7 })
        this.week.end = dateHelper.add(this.week.end!, { days: 7 })
      } else {
        this.week.start = dateHelper.substract(this.week.start!, { days: 7 })
        this.week.end = dateHelper.substract(this.week.end!, { days: 7 })
      }

      this.generateWeek()

      this.initialWeek = dateHelper.isDateInInterval(dateHelper.getDate(), {
        start: this.week.start,
        end: this.week.end,
      })
    },
    goToThisWeek(): void {
      this.week.start = dateHelper.startOf(dateHelper.getDate(), StartEndOf.WEEKS) || null
      this.week.end = dateHelper.add(this.week.start!, { days: 6 }) || null

      this.generateWeek()
      this.initialWeek = true
    },
    async generateWeek(): Promise<void> {
      const {
        name: type,
        params: { id },
        query: { language },
      } = this.$route

      try {
        let slotStart = this.week.start!

        if (dateHelper.isBefore(slotStart, dateHelper.getDate())) {
          slotStart = dateHelper.getDate()
        }

        if (this.collect.firstAvailableSlot && dateHelper.isBefore(slotStart, new Date(this.collect.firstAvailableSlot))) {
          slotStart = new Date(this.collect.firstAvailableSlot)
        }

        const { collect, slots: unfilteredSlots } = await CollectService.getSlots(
          type!,
          id,
          slotStart.toISOString(),
          this.week.end!.toISOString(),
          (this.language as any) || language?.toString(),
        )

        const slots = unfilteredSlots.filter((slot: {start: string}) => {
          if (this.collect.firstAvailableSlot && dateHelper.isBefore(new Date(slot.start), new Date(this.collect.firstAvailableSlot))) {
            return false
          }
          return true
        })

        if (collect.start) {
          this.selectedSlot = {
            start: collect.start,
            end: collect.end,
          }
        }

        this.weekDay = {}
        let now = this.week.start

        for (let localDay = now!.getDay(); localDay <= this.week.start!.getDay() + 6; localDay++) {
          this.weekDay = {
            ...this.weekDay,
            [dateHelper.getDayCode(now!)]: {
              slots: [],
              name: dateHelper.getDayCode(now!),
              date: now,
            },
          }

          now = dateHelper.add(now!, { days: 1 })
        }

        for (const slot of slots) {
          if (
            this.weekDay[dateHelper.getDayCode(slot.start)] &&
            !dateHelper.diff(
              new Date(this.weekDay[dateHelper.getDayCode(slot.start)].date),
              new Date(slot.start),
              OperationUnit.DAYS,
            )
          ) {
            let selected = false

            if (
              this.selectedSlot &&
              dateHelper.isSame(
                dateHelper.getDate(this.selectedSlot.start),
                dateHelper.getDate(slot.start),
              ) &&
              dateHelper.isSame(
                dateHelper.getDate(this.selectedSlot.end),
                dateHelper.getDate(slot.end),
              )
            ) {
              selected = true
            }
            this.weekDay[dateHelper.getDayCode(slot.start)].slots.push({ ...slot, selected })
          }
        }
      } catch (e) {
        //
      } finally {
        this.hasLoaded = true
      }
    },
    displaySlot(slot: Slot): string {
      return `${dateHelper.formatToTimezone(
        slot.start,
        this.collect.customer.timezone,
        'p',
      )} - ${dateHelper.formatToTimezone(slot.end, this.collect.customer.timezone, 'p')}`
    },
    getName(day: Day): string {
      if (this.initialWeek && day.date === dateHelper.getDate().getDate()) {
        return this.$t('common.today')
      } else if (this.initialWeek && day.date === dateHelper.getDate().getDate() + 1) {
        return this.$t('common.tomorrow')
      }
      return this.$t(`common.week.${day.name}`)
    },
    setSelectedSlot(slot: Slot, slotIndex: number, day: string): void {
      this.selectedSlot = slot

      for (const localDay in this.weekDay) {
        if (this.weekDay[localDay]) {
          for (const localSlot of this.weekDay[localDay].slots) {
            localSlot.selected = false
          }
        }
      }

      this.weekDay[day].slots[slotIndex].selected = true
    },
    isLastRow(dayIndex: string): boolean {
      return (
        dayIndex === dateHelper.getDayCode(this.week.end!) ||
        !this.weekDay[weekIndex[(weekIndex.findIndex(testDay => testDay === dayIndex) + 1) % 7]]
          ?.slots.length
      )
    },
    async submit() {
      try {
        await CollectService.patchSlot(this.$route.params.id, {
          slot: {
            start: this.selectedSlot!.start as string,
            end: this.selectedSlot!.end as string,
          },
        })
        this.$emit('success')
      } catch (e) {
        this.$store.dispatch('setSnackbar', {
          message: e.message.includes('409')
            ? this.$t('messages.collectSlotConflit')
            : this.$t('messages.error'),
          type: SnackBarType.ERROR,
        })
      }
    },
  },
})
